import React,{useCallback,useEffect,useMemo,useState}from"react";
import{
  Autocomplete,Box,Button,Dialog,DialogActions,DialogContent,
  DialogTitle,Divider,MenuItem,Paper,Table,TableBody,TableCell,
  TableContainer,TableHead,TableRow,TextField,Typography
}from"@mui/material";

const API_BASE="/api/purchases/nomina";

const MONTHS=[
  "Enero","Febrero","Marzo","Abril","Mayo","Junio",
  "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"
];

function today(){
  const date=new Date();
  return[
    date.getFullYear(),
    String(date.getMonth()+1).padStart(2,"0"),
    String(date.getDate()).padStart(2,"0")
  ].join("-");
}

function numberValue(value){
  const number=Number(String(value??"0").replace(",","."));
  return Number.isFinite(number)?number:0;
}

function money(value){
  return numberValue(value).toFixed(2);
}

function normalizeDecimal(value){
  let result=String(value??"").replace(/,/g,".").replace(/[^0-9.]/g,"");
  const index=result.indexOf(".");
  if(index!==-1){
    result=result.slice(0,index+1)+result.slice(index+1).replace(/\./g,"");
  }
  const parts=result.split(".");
  if(parts.length===2)result=`${parts[0]}.${parts[1].slice(0,2)}`;
  return result;
}

function dateLabel(value){
  if(!value)return"-";
  const parts=String(value).split("-");
  return parts.length===3?`${parts[2]}/${parts[1]}/${parts[0]}`:String(value);
}

function employeeName(employee){
  return employee?.razon_social||employee?.nombre_comercial||"";
}

function monthlyConcept(year,month){
  const index=Number(month)-1;
  if(index<0||index>11||!year)return"Pago de nómina";
  return`PAGO NÓMINA MES DE ${MONTHS[index].toUpperCase()} ${year}`;
}

function displayConcept(value,periodoDesde){
  const original=String(value||"").trim();
  const match=original.match(/^n[oó]mina\s+(\d{4})-(\d{1,2})$/i);
  if(match)return monthlyConcept(match[1],match[2]);
  if(!original&&periodoDesde){
    return monthlyConcept(periodoDesde.slice(0,4),periodoDesde.slice(5,7));
  }
  return original||"Pago de nómina";
}

function monthLabel(value){
  if(!value)return"-";
  const parts=String(value).split("-");
  const index=Number(parts[1])-1;
  return MONTHS[index]&&parts[0]?`${MONTHS[index]} ${parts[0]}`:"-";
}

function deliveryLabel(value){
  if(value==="CAJA")return"Caja";
  if(value==="EFECTIVO")return"Efectivo";
  if(value==="BANCO")return"Transferencia";
  return"-";
}

function initialForm(){
  const date=new Date();
  return{
    empleado_id:"",
    anio:String(date.getFullYear()),
    mes:String(date.getMonth()+1),
    fecha_nomina:today(),
    concepto:monthlyConcept(date.getFullYear(),date.getMonth()+1),
    sueldo_base:"0.00",
    otros_ingresos:"0.00",
    otros_descuentos:"0.00",
    observacion:""
  };
}

function initialConfig(){
  return{
    sueldo_mensual:"0.00",
    tarifa_hora_extra:"0.00"
  };
}

function monthEnd(year,month){
  const anio=Number(year);
  const mes=Number(month);
  if(!Number.isInteger(anio)||!Number.isInteger(mes)||mes<1||mes>12)return"";
  const last=new Date(anio,mes,0).getDate();
  return`${anio}-${String(mes).padStart(2,"0")}-${String(last).padStart(2,"0")}`;
}

function DecimalField({label,value,onChange,disabled=false,dashboardTheme}){
  return(
    <TextField
      fullWidth
      size="small"
      label={label}
      value={value}
      disabled={disabled}
      onChange={event=>onChange(normalizeDecimal(event.target.value))}
      onBlur={()=>onChange(money(value))}
      inputProps={{inputMode:"decimal"}}
      sx={dashboardTheme.inputStyles}
    />
  );
}

function SummaryRow({label,value,theme,strong=false,color}){
  return(
    <TableRow sx={theme.tableStyles.bodyRow}>
      <TableCell sx={{
        fontSize:13,
        fontWeight:strong?700:400,
        color:theme.text_color
      }}>
        {label}
      </TableCell>
      <TableCell align="right" sx={{
        fontSize:13,
        fontWeight:strong?700:500,
        color:color||theme.text_color,
        whiteSpace:"nowrap"
      }}>
        ${money(value)}
      </TableCell>
    </TableRow>
  );
}

export default function PayrollForm({
  empresaId,payrollId,isCreate,token,canCreate,canEdit,canDelete,
  dashboardTheme,navigate,showError,showSuccess,showInfo,
  outlinedButtonSx,dangerButtonSx,parseError
}){
  const[loading,setLoading]=useState(true);
  const[saving,setSaving]=useState(false);
  const[actionLoading,setActionLoading]=useState(false);
  const[configSaving,setConfigSaving]=useState(false);
  const[employees,setEmployees]=useState([]);
  const[configurations,setConfigurations]=useState([]);
  const[nomina,setNomina]=useState(null);
  const[form,setForm]=useState(initialForm);
  const[config,setConfig]=useState(initialConfig);
  const[horasExtras,setHorasExtras]=useState([]);
  const[anticipos,setAnticipos]=useState([]);
  const[annulDialogOpen,setAnnulDialogOpen]=useState(false);
  const[motivoAnulacion,setMotivoAnulacion]=useState("");

  const estado=nomina?.estado||"BORRADOR";
  const editable=!isCreate&&estado==="BORRADOR"&&canEdit;
  const registrado=estado==="REGISTRADO";
  const anulado=estado==="ANULADO";

  const fechaActual=today();
  const fechaCierre=isCreate
    ?monthEnd(form.anio,form.mes)
    :nomina?.periodo_hasta||"";

  const periodoFinalizado=Boolean(fechaCierre&&fechaCierre<=fechaActual);

  const selectedEmployee=useMemo(
    ()=>employees.find(item=>String(item.id)===String(form.empleado_id))||null,
    [employees,form.empleado_id]
  );

  const pendingOvertime=useMemo(
    ()=>horasExtras.filter(item=>item.estado==="PENDIENTE"),
    [horasExtras]
  );

  const overtimeTotal=useMemo(
    ()=>horasExtras
      .filter(item=>["APROBADA","LIQUIDADA"].includes(item.estado))
      .reduce((total,item)=>total+numberValue(item.total),0),
    [horasExtras]
  );

  const anticiposDelRol=useMemo(()=>{
    if(!nomina)return[];

    const aplicaciones=Array.isArray(nomina.anticipos_aplicados)
      ?nomina.anticipos_aplicados
      :[];

    const anticiposMap=new Map(
      anticipos.map(item=>[String(item.id),item])
    );

    return aplicaciones
      .map(aplicacion=>{
        const anticipo=anticiposMap.get(String(aplicacion.anticipo_id));

        if(!anticipo||anticipo.estado!=="ENTREGADO")return null;

        const aplicado=numberValue(aplicacion.valor);

        if(aplicado<=0)return null;

        return{
          id:aplicacion.id,
          fecha:anticipo.fecha,
          periodo_descuento:anticipo.periodo_descuento,
          monto:anticipo.monto,
          aplicado,
          forma_entrega:anticipo.forma_entrega,
          codigo_transferencia:anticipo.codigo_transferencia||""
        };
      })
      .filter(Boolean)
      .sort((a,b)=>String(a.fecha).localeCompare(String(b.fecha)));
  },[nomina,anticipos]);

  const totalAnticipos=useMemo(
    ()=>anticiposDelRol.reduce((total,item)=>total+item.aplicado,0),
    [anticiposDelRol]
  );

  const resumen=useMemo(()=>{
    if(!nomina)return null;

    const original=nomina.resumen||{};

    return{
      sueldo_base:original.sueldo_base??nomina.sueldo_base,
      horas_extras:original.horas_extras??nomina.horas_extras_total,
      otros_ingresos:original.otros_ingresos??nomina.otros_ingresos,
      total_ingresos:original.total_ingresos??nomina.total_ingresos,
      otros_descuentos:original.otros_descuentos??nomina.otros_descuentos,
      anticipos_aplicados:original.anticipos_aplicados??nomina.anticipos_descontados,
      total_descuentos:original.total_descuentos??nomina.total_descuentos,
      neto:original.neto??nomina.total,
      total_pagado:original.total_pagado??nomina.total_pagado,
      saldo_pendiente:original.saldo_pendiente??nomina.saldo_pendiente
    };
  },[nomina]);

  const estimatedIncome=
    numberValue(form.sueldo_base)+
    numberValue(form.otros_ingresos)+
    overtimeTotal;

  const estimatedDiscounts=
    numberValue(form.otros_descuentos)+
    numberValue(nomina?.anticipos_descontados);

  const unsavedChanges=useMemo(()=>{
    if(!nomina||isCreate)return false;

    return[
      ["sueldo_base",form.sueldo_base],
      ["otros_ingresos",form.otros_ingresos],
      ["otros_descuentos",form.otros_descuentos]
    ].some(([key,value])=>
      Math.abs(numberValue(value)-numberValue(nomina[key]))>0.009
    )||
    form.fecha_nomina!==(nomina.fecha_nomina||"")||
    form.concepto.trim()!==displayConcept(
      nomina.concepto,nomina.periodo_desde
    ).trim()||
    form.observacion!==String(nomina.observacion||"");
  },[nomina,isCreate,form]);

  const request=useCallback(async(endpoint,options={})=>{
    const response=await fetch(endpoint,{
      ...options,
      credentials:"include",
      headers:{
        ...(token?{Authorization:`Bearer ${token}`}:{ }),
        ...(options.body?{"Content-Type":"application/json"}:{}),
        ...(options.headers||{})
      }
    });

    const contentType=response.headers.get("content-type")||"";
    const data=contentType.includes("application/json")
      ?await response.json()
      :{error:"Respuesta inválida del servidor."};

    if(!response.ok){
      const error=data?.error||data?.detail||data?.mensaje;
      const message=typeof error==="string"
        ?error
        :error&&typeof error==="object"
          ?Object.values(error).flat().join(" ")
          :parseError(data);

      throw new Error(message);
    }

    return data;
  },[token,parseError]);

  const loadCatalogs=useCallback(async()=>{
    const params=new URLSearchParams({empresa_id:String(empresaId)});
    const data=await request(`${API_BASE}/catalogos/?${params}`);

    setEmployees(Array.isArray(data.empleados)?data.empleados:[]);
    setConfigurations(
      Array.isArray(data.configuraciones)?data.configuraciones:[]
    );

    return data;
  },[empresaId,request]);

  const loadPayroll=useCallback(async()=>{
    if(!payrollId)return null;

    const params=new URLSearchParams({empresa_id:String(empresaId)});
    const data=await request(`${API_BASE}/${payrollId}/?${params}`);
    const item=data.nomina;

    setNomina(item);
    setForm({
      empleado_id:item.empleado_id||"",
      anio:String(item.periodo_desde?.slice(0,4)||new Date().getFullYear()),
      mes:String(Number(item.periodo_desde?.slice(5,7))||new Date().getMonth()+1),
      fecha_nomina:item.fecha_nomina||today(),
      concepto:displayConcept(item.concepto,item.periodo_desde),
      sueldo_base:money(item.sueldo_base),
      otros_ingresos:money(item.otros_ingresos),
      otros_descuentos:money(item.otros_descuentos),
      observacion:item.observacion||""
    });

    return item;
  },[empresaId,payrollId,request]);

  const loadMovements=useCallback(async(item)=>{
    if(!item?.empleado_id){
      setHorasExtras([]);
      setAnticipos([]);
      return;
    }

    const params=new URLSearchParams({
      empresa_id:String(empresaId),
      empleado_id:String(item.empleado_id)
    });

    const overtimeParams=new URLSearchParams({
      empresa_id:String(empresaId),
      empleado_id:String(item.empleado_id),
      fecha_desde:item.periodo_desde,
      fecha_hasta:item.periodo_hasta
    });

    const[overtimeData,advancesData]=await Promise.all([
      request(`${API_BASE}/horas-extras/?${overtimeParams}`),
      request(`${API_BASE}/anticipos/?${params}`)
    ]);

    const overtimeResults=Array.isArray(overtimeData.results)
      ?overtimeData.results
      :[];

    setHorasExtras(
      overtimeResults.filter(extra=>extra.estado!=="ANULADA")
    );

    setAnticipos(
      Array.isArray(advancesData.results)
        ?advancesData.results
        :[]
    );
  },[empresaId,request]);

  const refreshPayroll=useCallback(async()=>{
    const item=await loadPayroll();
    if(item)await loadMovements(item);
    return item;
  },[loadPayroll,loadMovements]);

  useEffect(()=>{
    let active=true;

    const initialize=async()=>{
      try{
        setLoading(true);
        await loadCatalogs();

        if(!isCreate){
          await refreshPayroll();
        }else{
          setNomina(null);
          setHorasExtras([]);
          setAnticipos([]);
          setForm(initialForm());
        }
      }catch(error){
        if(active)showError(error.message);
      }finally{
        if(active)setLoading(false);
      }
    };

    if(empresaId)initialize();
    else setLoading(false);

    return()=>{active=false;};
  },[empresaId,payrollId,isCreate,loadCatalogs,refreshPayroll,showError]);

  useEffect(()=>{
    if(!form.empleado_id){
      setConfig(initialConfig());
      return;
    }

    const item=configurations.find(configuration=>
      String(configuration.empleado_id)===String(form.empleado_id)
    );

    setConfig({
      sueldo_mensual:money(item?.sueldo_mensual),
      tarifa_hora_extra:money(item?.tarifa_hora_extra)
    });
  },[form.empleado_id,configurations]);

  const formSet=(key,value)=>{
    setForm(previous=>({...previous,[key]:value}));
  };

  const configSet=(key,value)=>{
    setConfig(previous=>({...previous,[key]:value}));
  };

  const goBack=()=>navigate("/admin-panel/nomina/roles");

  const saveConfiguration=async()=>{
    if(!form.empleado_id){
      showInfo("Selecciona un empleado.");
      return;
    }

    if(
      numberValue(config.sueldo_mensual)<0||
      numberValue(config.tarifa_hora_extra)<0
    ){
      showInfo("Los valores no pueden ser negativos.");
      return;
    }

    try{
      setConfigSaving(true);

      const data=await request(`${API_BASE}/configuraciones/`,{
        method:"POST",
        body:JSON.stringify({
          empresa_id:empresaId,
          empleado_id:form.empleado_id,
          sueldo_mensual:money(config.sueldo_mensual),
          tarifa_hora_extra:money(config.tarifa_hora_extra),
          activo:true
        })
      });

      showSuccess(data.mensaje||"Configuración guardada correctamente.");
      await loadCatalogs();
    }catch(error){
      showError(error.message);
    }finally{
      setConfigSaving(false);
    }
  };

  const generatePayroll=async()=>{
    if(!form.empleado_id){
      showInfo("Selecciona un empleado.");
      return;
    }

    const anio=Number(form.anio);
    const mes=Number(form.mes);

    if(
      !Number.isInteger(anio)||anio<2000||anio>2100||
      !Number.isInteger(mes)||mes<1||mes>12
    ){
      showInfo("Selecciona un período válido.");
      return;
    }

    try{
      setSaving(true);

      const data=await request(`${API_BASE}/generar-mensual/`,{
        method:"POST",
        body:JSON.stringify({
          empresa_id:empresaId,
          empleado_id:form.empleado_id,
          anio,
          mes
        })
      });

      if(data.created){
        showSuccess("Borrador mensual generado correctamente.");
      }else{
        showInfo("Ya existe un rol para este empleado y período.");
      }

      if(data.nomina?.id){
        navigate(`/admin-panel/nomina/${data.nomina.id}`,{replace:true});
      }
    }catch(error){
      showError(error.message);
    }finally{
      setSaving(false);
    }
  };

  const savePayroll=async()=>{
    if(!payrollId||!editable||saving)return;

    if(
      numberValue(form.sueldo_base)<0||
      numberValue(form.otros_ingresos)<0||
      numberValue(form.otros_descuentos)<0
    ){
      showInfo("Los importes no pueden ser negativos.");
      return;
    }

    if(estimatedIncome-estimatedDiscounts<0){
      showInfo("Los descuentos superan los ingresos.");
      return;
    }

    if(!form.concepto.trim()){
      showInfo("Ingresa un concepto.");
      return;
    }

    try{
      setSaving(true);

      const data=await request(`${API_BASE}/${payrollId}/`,{
        method:"PATCH",
        body:JSON.stringify({
          empresa_id:empresaId,
          fecha_nomina:form.fecha_nomina,
          concepto:form.concepto.trim(),
          sueldo_base:money(form.sueldo_base),
          otros_ingresos:money(form.otros_ingresos),
          otros_descuentos:money(form.otros_descuentos),
          observacion:form.observacion
        })
      });

      showSuccess(data.mensaje||"Borrador actualizado correctamente.");
      await refreshPayroll();
    }catch(error){
      showError(error.message);
    }finally{
      setSaving(false);
    }
  };

  const registerPayroll=async()=>{
    if(!payrollId||!editable||actionLoading||saving)return;

    if(!periodoFinalizado){
      showInfo("El período todavía no ha finalizado.");
      return;
    }

    if(pendingOvertime.length>0){
      showInfo("Existen horas extras pendientes de aprobación.");
      return;
    }

    if(unsavedChanges){
      showInfo("Guarda primero los cambios del rol.");
      return;
    }

    try{
      setActionLoading(true);

      const data=await request(`${API_BASE}/${payrollId}/registrar/`,{
        method:"POST",
        body:JSON.stringify({empresa_id:empresaId})
      });

      showSuccess(data.mensaje||"Rol registrado correctamente.");
      await refreshPayroll();
    }catch(error){
      showError(error.message);
    }finally{
      setActionLoading(false);
    }
  };

  const annulPayroll=async()=>{
    if(!motivoAnulacion.trim()){
      showInfo("Ingresa el motivo de anulación.");
      return;
    }

    try{
      setActionLoading(true);

      const data=await request(`${API_BASE}/${payrollId}/anular/`,{
        method:"POST",
        body:JSON.stringify({
          empresa_id:empresaId,
          motivo:motivoAnulacion.trim()
        })
      });

      showSuccess(data.mensaje||"Rol anulado correctamente.");
      setAnnulDialogOpen(false);
      setMotivoAnulacion("");
      await refreshPayroll();
    }catch(error){
      showError(error.message);
    }finally{
      setActionLoading(false);
    }
  };

  const buttonSx={
    ...dashboardTheme.buttonStyles.contained,
    minHeight:35,
    px:2,
    borderRadius:"9px",
    textTransform:"none",
    fontSize:13,
    fontWeight:600,
    boxShadow:"none"
  };

  const secondaryButtonSx={
    ...outlinedButtonSx,
    minHeight:35,
    fontWeight:600,
    textTransform:"none",
    boxShadow:"none"
  };

  const cardSx={
    p:{xs:1.5,md:2},
    bgcolor:dashboardTheme.card_background,
    border:`1px solid ${dashboardTheme.border_color}`,
    borderRadius:"12px",
    boxShadow:"none"
  };

  const fieldGridSx={
    display:"grid",
    gridTemplateColumns:{xs:"1fr",sm:"repeat(2,minmax(0,1fr))"},
    gap:1.5
  };

  const sectionTitleSx={
    fontSize:16,
    fontWeight:700,
    color:dashboardTheme.text_color,
    mb:1.5
  };

  const mutedSx={
    fontSize:12.5,
    color:dashboardTheme.muted_text_color
  };

  const tableStyles=dashboardTheme.tableStyles;
  const tableHeaderSx=tableStyles.headCell;
  const tableRowSx=tableStyles.bodyRow;
  const tableWrapperSx={...tableStyles.wrapper,boxShadow:"none"};

  if(loading){
    return(
      <Typography sx={{
        py:4,
        textAlign:"center",
        fontSize:14,
        color:dashboardTheme.muted_text_color
      }}>
        Cargando rol de pago...
      </Typography>
    );
  }

  return(
    <>
      <Box sx={{
        display:"flex",
        alignItems:{xs:"stretch",sm:"center"},
        justifyContent:"space-between",
        flexDirection:{xs:"column",sm:"row"},
        gap:1.5,
        mb:2
      }}>
        <Box>
          <Typography sx={{
            fontSize:{xs:24,sm:26,md:30},
            fontWeight:600,
            color:dashboardTheme.primary_color,
            lineHeight:1.2
          }}>
            {isCreate?"Generar rol mensual":"Detalle del rol de pago"}
          </Typography>

          {!isCreate&&nomina&&(
            <Typography sx={{...mutedSx,mt:.5}}>
              {employeeName(nomina.empleado)}
              {" · "}
              {estado==="BORRADOR"?"Borrador":registrado?"Registrado":"Anulado"}
              {" · "}
              {dateLabel(nomina.periodo_desde)}
              {" - "}
              {dateLabel(nomina.periodo_hasta)}
            </Typography>
          )}
        </Box>

        <Box sx={{display:"flex",flexWrap:"wrap",gap:1}}>
          <Button
            variant="outlined"
            onClick={goBack}
            sx={secondaryButtonSx}
          >
            Regresar
          </Button>

          {!isCreate&&registrado&&(
            <Button
              variant="contained"
              onClick={()=>navigate(`/admin-panel/nomina/${payrollId}/pagos`)}
              sx={buttonSx}
            >
              Ver pagos
            </Button>
          )}
        </Box>
      </Box>

      {isCreate?(
        <>
          <Paper elevation={0} sx={{...cardSx,mb:2}}>
            <Typography sx={sectionTitleSx}>
              Empleado y período
            </Typography>

            <Box sx={fieldGridSx}>
              <Autocomplete
                options={employees}
                value={selectedEmployee}
                onChange={(_event,value)=>formSet("empleado_id",value?.id||"")}
                getOptionLabel={item=>{
                  const identification=item.identificacion||item.cedula||item.ruc||"";
                  return`${employeeName(item)}${identification?` - ${identification}`:""}`;
                }}
                isOptionEqualToValue={(option,value)=>String(option.id)===String(value.id)}
                filterOptions={(options,{inputValue})=>{
                  const query=inputValue.toLowerCase().trim();
                  if(!query)return options.slice(0,50);
                  return options.filter(item=>
                    employeeName(item).toLowerCase().includes(query)||
                    String(item.identificacion||"").includes(query)||
                    String(item.cedula||"").includes(query)
                  ).slice(0,50);
                }}
                renderInput={params=>(
                  <TextField
                    {...params}
                    size="small"
                    label="Empleado"
                    placeholder="Buscar empleado..."
                    sx={dashboardTheme.inputStyles}
                  />
                )}
                noOptionsText="No se encontraron empleados"
              />

              <TextField
                fullWidth
                size="small"
                type="number"
                label="Año"
                value={form.anio}
                onChange={event=>{
                  const anio=event.target.value;
                  setForm(previous=>({
                    ...previous,
                    anio,
                    concepto:monthlyConcept(anio,previous.mes)
                  }));
                }}
                inputProps={{min:2000,max:2100}}
                sx={dashboardTheme.inputStyles}
              />

              <TextField
                fullWidth
                size="small"
                select
                label="Mes"
                value={form.mes}
                onChange={event=>{
                  const mes=event.target.value;
                  setForm(previous=>({
                    ...previous,
                    mes,
                    concepto:monthlyConcept(previous.anio,mes)
                  }));
                }}
                SelectProps={{MenuProps:{disableScrollLock:true}}}
                sx={dashboardTheme.inputStyles}
              >
                {MONTHS.map((month,index)=>(
                  <MenuItem key={index+1} value={String(index+1)}>
                    {month}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Paper>

          {form.empleado_id&&(
            <Paper elevation={0} sx={{...cardSx,mb:2}}>
              <Typography sx={sectionTitleSx}>
                Configuración del empleado
              </Typography>

              <Box sx={fieldGridSx}>
                <DecimalField
                  label="Sueldo mensual"
                  value={config.sueldo_mensual}
                  onChange={value=>configSet("sueldo_mensual",value)}
                  dashboardTheme={dashboardTheme}
                />

                <DecimalField
                  label="Tarifa habitual de hora extra"
                  value={config.tarifa_hora_extra}
                  onChange={value=>configSet("tarifa_hora_extra",value)}
                  dashboardTheme={dashboardTheme}
                />
              </Box>

              <Box sx={{
                display:"flex",
                justifyContent:"flex-end",
                mt:2
              }}>
                <Button
                  variant="contained"
                  onClick={saveConfiguration}
                  disabled={configSaving||!canEdit}
                  sx={buttonSx}
                >
                  {configSaving?"Guardando...":"Guardar configuración"}
                </Button>
              </Box>
            </Paper>
          )}

          <Box sx={{
            display:"flex",
            justifyContent:"flex-end",
            mt:2
          }}>
            <Button
              variant="contained"
              onClick={generatePayroll}
              disabled={saving||!form.empleado_id||!canCreate||configSaving}
              sx={buttonSx}
            >
              {saving?"Generando...":"Generar borrador"}
            </Button>
          </Box>
        </>
      ):nomina?(
        <>
          <Paper elevation={0} sx={{...cardSx,mb:2}}>
            <Typography sx={sectionTitleSx}>
              Datos del rol
            </Typography>

            <Box sx={fieldGridSx}>
              <TextField
                fullWidth
                size="small"
                label="Empleado"
                value={employeeName(nomina.empleado)}
                disabled
                sx={dashboardTheme.inputStyles}
              />

              <TextField
                fullWidth
                size="small"
                type="date"
                label="Fecha de nómina"
                value={form.fecha_nomina}
                disabled={!editable}
                onChange={event=>formSet("fecha_nomina",event.target.value)}
                InputLabelProps={{shrink:true}}
                sx={dashboardTheme.inputStyles}
              />

              <TextField
                fullWidth
                size="small"
                type="date"
                label="Período desde"
                value={nomina.periodo_desde||""}
                disabled
                InputLabelProps={{shrink:true}}
                sx={dashboardTheme.inputStyles}
              />

              <TextField
                fullWidth
                size="small"
                type="date"
                label="Período hasta"
                value={nomina.periodo_hasta||""}
                disabled
                InputLabelProps={{shrink:true}}
                sx={dashboardTheme.inputStyles}
              />

              <TextField
                fullWidth
                size="small"
                label="Concepto"
                value={form.concepto}
                disabled={!editable}
                onChange={event=>formSet("concepto",event.target.value)}
                sx={dashboardTheme.inputStyles}
              />
            </Box>

            {editable&&(
              <TextField
                fullWidth
                size="small"
                label="Observación (opcional)"
                value={form.observacion}
                onChange={event=>formSet("observacion",event.target.value)}
                sx={{mt:1.5,...dashboardTheme.inputStyles}}
              />
            )}

            {!editable&&form.observacion&&(
              <Typography sx={{...mutedSx,mt:1.5}}>
                {form.observacion}
              </Typography>
            )}
          </Paper>

          <Paper elevation={0} sx={{...cardSx,mb:2}}>
            <Typography sx={sectionTitleSx}>
              Ingresos y descuentos
            </Typography>

            <Box sx={fieldGridSx}>
              <DecimalField
                label="Sueldo base del período"
                value={form.sueldo_base}
                onChange={value=>formSet("sueldo_base",value)}
                disabled={!editable}
                dashboardTheme={dashboardTheme}
              />

              <DecimalField
                label="Otros ingresos"
                value={form.otros_ingresos}
                onChange={value=>formSet("otros_ingresos",value)}
                disabled={!editable}
                dashboardTheme={dashboardTheme}
              />

              <DecimalField
                label="Otros descuentos"
                value={form.otros_descuentos}
                onChange={value=>formSet("otros_descuentos",value)}
                disabled={!editable}
                dashboardTheme={dashboardTheme}
              />

              <TextField
                fullWidth
                size="small"
                label="Horas extras aprobadas"
                value={money(overtimeTotal)}
                disabled
                sx={dashboardTheme.inputStyles}
              />
            </Box>

            {editable&&(
              <Box sx={{
                display:"flex",
                justifyContent:"flex-end",
                mt:2
              }}>
                <Button
                  variant="contained"
                  onClick={savePayroll}
                  disabled={saving||actionLoading||!unsavedChanges}
                  sx={buttonSx}
                >
                  {saving?"Guardando...":"Guardar cambios"}
                </Button>
              </Box>
            )}
          </Paper>

          <Paper elevation={0} sx={{...cardSx,mb:2}}>
            <Typography sx={sectionTitleSx}>
              Horas extras del período
            </Typography>

            <Paper elevation={0} sx={tableWrapperSx}>
              <Box sx={tableStyles.tableArea}>
                <TableContainer sx={tableStyles.container}>
                  <Table size="small" sx={tableStyles.table}>
                    <TableHead>
                      <TableRow sx={tableStyles.headRow}>
                        <TableCell sx={tableHeaderSx}>Fecha</TableCell>
                        <TableCell sx={tableHeaderSx}>Horario</TableCell>
                        <TableCell align="right" sx={tableHeaderSx}>Descanso</TableCell>
                        <TableCell align="right" sx={tableHeaderSx}>Horas</TableCell>
                        <TableCell align="right" sx={tableHeaderSx}>Tarifa</TableCell>
                        <TableCell align="right" sx={tableHeaderSx}>Total</TableCell>
                        <TableCell sx={tableHeaderSx}>Estado</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {horasExtras.map(item=>(
                        <TableRow key={item.id} sx={tableRowSx}>
                          <TableCell>{dateLabel(item.fecha)}</TableCell>

                          <TableCell sx={{whiteSpace:"nowrap"}}>
                            {String(item.hora_inicio||"").slice(0,5)}
                            {" - "}
                            {String(item.hora_fin||"").slice(0,5)}
                          </TableCell>

                          <TableCell align="right">
                            {item.descuento_almuerzo_minutos} min
                          </TableCell>

                          <TableCell align="right">
                            {money(item.horas_calculadas)}
                          </TableCell>

                          <TableCell align="right">
                            ${money(item.tarifa_hora)}
                          </TableCell>

                          <TableCell align="right">
                            ${money(item.total)}
                          </TableCell>

                          <TableCell>
                            <Typography sx={{
                              fontSize:12,
                              fontWeight:600,
                              color:item.estado==="PENDIENTE"
                                ?"#D97706"
                                :dashboardTheme.text_color
                            }}>
                              {item.estado==="PENDIENTE"
                                ?"Pendiente"
                                :item.estado==="APROBADA"
                                  ?"Aprobada"
                                  :item.estado==="LIQUIDADA"
                                    ?"Liquidada"
                                    :item.estado}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}

                      {horasExtras.length===0&&(
                        <TableRow>
                          <TableCell
                            colSpan={7}
                            align="center"
                            sx={tableStyles.emptyCell}
                          >
                            No existen horas extras para este período.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>

            {pendingOvertime.length>0&&(
              <Typography sx={{
                ...mutedSx,
                mt:1.5,
                color:"#D97706"
              }}>
                Hay {pendingOvertime.length} registro(s) pendiente(s) de aprobación.
              </Typography>
            )}
          </Paper>

          <Paper elevation={0} sx={{...cardSx,mb:2}}>
            <Typography sx={sectionTitleSx}>
              Anticipos del período
            </Typography>

            <Paper elevation={0} sx={tableWrapperSx}>
              <Box sx={tableStyles.tableArea}>
                <TableContainer sx={tableStyles.container}>
                  <Table size="small" sx={tableStyles.table}>
                    <TableHead>
                      <TableRow sx={tableStyles.headRow}>
                        <TableCell sx={tableHeaderSx}>Fecha de entrega</TableCell>
                        <TableCell sx={tableHeaderSx}>Período de descuento</TableCell>
                        <TableCell sx={tableHeaderSx}>Método</TableCell>
                        <TableCell sx={tableHeaderSx}>Referencia</TableCell>
                        <TableCell align="right" sx={tableHeaderSx}>Entregado</TableCell>
                        <TableCell align="right" sx={tableHeaderSx}>Aplicado al rol</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {anticiposDelRol.map(item=>(
                        <TableRow key={item.id} sx={tableRowSx}>
                          <TableCell sx={{whiteSpace:"nowrap"}}>
                            {dateLabel(item.fecha)}
                          </TableCell>

                          <TableCell sx={{whiteSpace:"nowrap"}}>
                            {monthLabel(item.periodo_descuento)}
                          </TableCell>

                          <TableCell>
                            {deliveryLabel(item.forma_entrega)}
                          </TableCell>

                          <TableCell>
                            {item.codigo_transferencia||"-"}
                          </TableCell>

                          <TableCell align="right">
                            ${money(item.monto)}
                          </TableCell>

                          <TableCell align="right">
                            <Typography sx={{
                              fontSize:13,
                              fontWeight:700,
                              color:dashboardTheme.text_color
                            }}>
                              ${money(item.aplicado)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}

                      {anticiposDelRol.length===0&&(
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            align="center"
                            sx={tableStyles.emptyCell}
                          >
                            No existen anticipos aplicados a este rol.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>

          </Paper>

          <Paper elevation={0} sx={{...cardSx,mb:2}}>
            <Typography sx={sectionTitleSx}>
              Resumen del rol de pago
            </Typography>

            <Paper elevation={0} sx={tableWrapperSx}>
              <Box sx={tableStyles.tableArea}>
                <TableContainer sx={tableStyles.container}>
                  <Table size="small" sx={tableStyles.table}>
                    <TableHead>
                      <TableRow sx={tableStyles.headRow}>
                        <TableCell sx={tableHeaderSx}>Concepto</TableCell>
                        <TableCell align="right" sx={tableHeaderSx}>Valor</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      <SummaryRow
                        label="Sueldo base"
                        value={resumen.sueldo_base}
                        theme={dashboardTheme}
                      />

                      <SummaryRow
                        label="Horas extras"
                        value={resumen.horas_extras}
                        theme={dashboardTheme}
                      />

                      <SummaryRow
                        label="Otros ingresos"
                        value={resumen.otros_ingresos}
                        theme={dashboardTheme}
                      />

                      <SummaryRow
                        label="Total ingresos"
                        value={resumen.total_ingresos}
                        theme={dashboardTheme}
                        strong
                      />

                      <SummaryRow
                        label="Otros descuentos"
                        value={resumen.otros_descuentos}
                        theme={dashboardTheme}
                      />

                      <SummaryRow
                        label="Anticipos aplicados"
                        value={resumen.anticipos_aplicados}
                        theme={dashboardTheme}
                      />

                      <SummaryRow
                        label="Total descuentos"
                        value={resumen.total_descuentos}
                        theme={dashboardTheme}
                        strong
                      />

                      <SummaryRow
                        label="Neto del rol"
                        value={resumen.neto}
                        theme={dashboardTheme}
                        strong
                        color={dashboardTheme.primary_color}
                      />

                      <SummaryRow
                        label="Pagos realizados"
                        value={resumen.total_pagado}
                        theme={dashboardTheme}
                      />

                      <SummaryRow
                        label="Saldo pendiente de pago"
                        value={resumen.saldo_pendiente}
                        theme={dashboardTheme}
                        strong
                        color={
                          numberValue(resumen.saldo_pendiente)>0
                            ?"#D97706"
                            :dashboardTheme.text_color
                        }
                      />
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>

            <Divider sx={{
              my:2,
              borderColor:dashboardTheme.border_color
            }}/>

            <Box sx={{
              display:"flex",
              justifyContent:"flex-end",
              flexWrap:"wrap",
              gap:1
            }}>
              {!anulado&&canDelete&&(
                <Button
                  variant="outlined"
                  onClick={()=>setAnnulDialogOpen(true)}
                  disabled={actionLoading||saving}
                  sx={dangerButtonSx}
                >
                  Anular rol
                </Button>
              )}

              {editable&&(
                <Button
                  variant="contained"
                  onClick={registerPayroll}
                  disabled={
                    actionLoading||
                    saving||
                    pendingOvertime.length>0||
                    !periodoFinalizado||
                    unsavedChanges
                  }
                  sx={buttonSx}
                >
                  {actionLoading?"Procesando...":"Confirmar cierre mensual"}
                </Button>
              )}

              {registrado&&(
                <Button
                  variant="contained"
                  onClick={()=>navigate(`/admin-panel/nomina/${payrollId}/pagos`)}
                  sx={buttonSx}
                >
                  Ver pagos
                </Button>
              )}
            </Box>
          </Paper>
        </>
      ):(
        <Typography sx={mutedSx}>
          No se encontró el rol solicitado.
        </Typography>
      )}

      <Dialog
        open={annulDialogOpen}
        onClose={()=>!actionLoading&&setAnnulDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          elevation:0,
          sx:{
            bgcolor:dashboardTheme.card_background,
            color:dashboardTheme.text_color,
            border:`1px solid ${dashboardTheme.border_color}`,
            borderRadius:"12px",
            boxShadow:"none"
          }
        }}
      >
        <DialogTitle sx={{
          fontSize:18,
          fontWeight:700,
          color:dashboardTheme.text_color
        }}>
          Anular rol de pago
        </DialogTitle>

        <DialogContent>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={3}
            label="Motivo de anulación"
            value={motivoAnulacion}
            onChange={event=>setMotivoAnulacion(event.target.value)}
            sx={{mt:1,...dashboardTheme.inputStyles}}
          />
        </DialogContent>

        <DialogActions sx={{p:2,gap:.7}}>
          <Button
            variant="outlined"
            onClick={()=>setAnnulDialogOpen(false)}
            disabled={actionLoading}
            sx={secondaryButtonSx}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={annulPayroll}
            disabled={actionLoading||!motivoAnulacion.trim()}
            sx={{
              ...buttonSx,
              bgcolor:"#D32F2F",
              "&:hover":{bgcolor:"#B71C1C",boxShadow:"none"}
            }}
          >
            {actionLoading?"Anulando...":"Confirmar anulación"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}